import { useCallback, useEffect, useRef, useState } from "react";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { FaWhatsapp } from "react-icons/fa";

import ScrollToTop from "./components/ScrollToTop";
import CookieConsentBanner from "./components/CookieConsentBanner";
import Footer from "./components/Footer";
import MobileCTABar from "./components/MobileCTABar";
import Navbar from "./components/Navbar";
import ProtectedAdminRoute from "./components/ProtectedAdminRoute";
import SeoSchema from "./components/SeoSchema";
import AdminPage from "./pages/AdminPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import UpdatePasswordPage from "./pages/UpdatePasswordPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import SettingsPage from "./pages/SettingsPage";


import HomePage from "./pages/HomePage";
import CyprusPage from "./pages/CyprusPage";
import AirportTransferPage from "./pages/AirportTransferPage";
import ThingsToDoLimassolPage from "./pages/ThingsToDoLimassolPage";
import LarnacaAirportToLimassolPage from "./pages/LarnacaAirportToLimassolPage";
import PaphosAirportToLimassolPage from "./pages/PaphosAirportToLimassolPage";
import LimassolToLarnacaAirportPage from "./pages/LimassolToLarnacaAirportPage";
import LimassolToPaphosAirportPage from "./pages/LimassolToPaphosAirportPage";
import TaxiLimassolPage from "./pages/TaxiLimassolPage";
import LimassolHotelsPage from "./pages/LimassolHotelsPage";
import CyprusAirportTransfersPage from "./pages/CyprusAirportTransfersPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import { supabase } from "./lib/supabaseClient";
import { trackPageView, trackWhatsAppClick } from "./utils/analytics";

function AdminSessionTimeout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const resetTimerRef = useRef(null);

  const isAdminPage = location.pathname.startsWith("/admin");
  const inactivityLimit = 30 * 60 * 1000;
  const warningLeadTime = 60 * 1000;

  const clearInactivityTimers = useCallback(() => {
    clearTimeout(warningTimerRef.current);
    clearTimeout(logoutTimerRef.current);
  }, []);

  const signOutAndRedirect = useCallback(async () => {
    clearInactivityTimers();
    setShowWarning(false);
    await supabase.auth.signOut();
    navigate("/admin-login");
  }, [clearInactivityTimers, navigate]);

  useEffect(() => {
    if (!isAdminPage) {
      clearInactivityTimers();
      setShowWarning(false);
      resetTimerRef.current = null;
      return;
    }

    const activityEvents = ["mousemove", "keydown", "click", "scroll"];

    const resetInactivityTimer = () => {
      clearInactivityTimers();
      setShowWarning(false);

      warningTimerRef.current = setTimeout(() => {
        setShowWarning(true);
      }, inactivityLimit - warningLeadTime);

      logoutTimerRef.current = setTimeout(() => {
        signOutAndRedirect();
      }, inactivityLimit);
    };

    resetTimerRef.current = resetInactivityTimer;
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, resetInactivityTimer);
    });
    resetInactivityTimer();

    return () => {
      clearInactivityTimers();
      resetTimerRef.current = null;
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, resetInactivityTimer);
      });
    };
  }, [clearInactivityTimers, inactivityLimit, isAdminPage, signOutAndRedirect, warningLeadTime]);

  if (!showWarning || !isAdminPage) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 px-6 text-white">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-neutral-950 p-8 shadow-2xl">
        <h2 className="text-2xl font-bold text-yellow-400">
          Session Expiring
        </h2>

        <p className="mt-4 text-neutral-300">
          You will be logged out in 1 minute due to inactivity.
        </p>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={() => resetTimerRef.current?.()}
            className="rounded-2xl bg-yellow-400 px-6 py-4 font-bold text-black hover:bg-yellow-300"
          >
            Stay Logged In
          </button>

          <button
            type="button"
            onClick={signOutAndRedirect}
            className="rounded-2xl border border-white/10 bg-white/[0.04] px-6 py-4 font-bold text-white hover:bg-white/[0.08]"
          >
            Logout Now
          </button>
        </div>
      </div>
    </div>
  );
}

function PublicPageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    if (!location.pathname.startsWith("/admin") && location.pathname !== "/update-password") {
      trackPageView(location.pathname + location.search);
    }
  }, [location.pathname, location.search]);

  return null;
}

function AppShell() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isAdminShell =
    location.pathname.startsWith("/admin") ||
    location.pathname === "/update-password";

  return (
    <>
      <ScrollToTop />
      <AdminSessionTimeout />
      <PublicPageViewTracker />
      <div className="min-h-screen bg-[#020617]">
        {!isAdminShell && (
          <Navbar onMenuOpenChange={setMobileMenuOpen} />
        )}
        {!isAdminShell && <SeoSchema />}

        <Routes>
  <Route path="/" element={<HomePage />} />

  <Route path="/cyprus" element={<CyprusPage />} />

  <Route path="/things-to-do-in-limassol" element={<ThingsToDoLimassolPage />} />

  <Route path="/larnaca-airport-to-limassol" element={<LarnacaAirportToLimassolPage />} />

  <Route path="/paphos-airport-to-limassol" element={<PaphosAirportToLimassolPage />} />

  <Route path="/limassol-to-larnaca-airport-taxi" element={<LimassolToLarnacaAirportPage />} />

  <Route path="/limassol-to-paphos-airport-taxi" element={<LimassolToPaphosAirportPage />} />

  <Route path="/taxi-limassol" element={<TaxiLimassolPage />} />

  <Route path="/limassol-hotels" element={<LimassolHotelsPage />} />

  <Route path="/cyprus-airport-transfers" element={<CyprusAirportTransfersPage />} />

  <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

  <Route path="/terms-and-conditions" element={<TermsPage />} />

  <Route path="/cookie-policy" element={<CookiePolicyPage />} />

  <Route path="/admin-login" element={<AdminLoginPage />} />

  <Route element={<ProtectedAdminRoute />}>
    <Route path="/admin" element={<AdminPage />} />
    <Route path="/admin/analytics" element={<AnalyticsPage />} />
    <Route path="/admin/settings" element={<SettingsPage />} />
    <Route path="/update-password" element={<UpdatePasswordPage />} />
  </Route>


  <Route
    path="/larnaca-airport-taxi"
    element={
      <AirportTransferPage
        airportName="Larnaca Airport"
        routeTitle="Limassol to Larnaca Airport Taxi"
        price="From €70"
        description="Book a reliable private taxi transfer from Limassol to Larnaca Airport. Available 24/7 with fast WhatsApp booking."
        canonicalUrl="https://limassolairporttaxi.com/larnaca-airport-taxi"
        ogUrl="https://limassolairporttaxi.com/larnaca-airport-taxi"
      />
    }
  />

  <Route
    path="/paphos-airport-taxi"
    element={
      <AirportTransferPage
        airportName="Paphos Airport"
        routeTitle="Limassol to Paphos Airport Taxi"
        price="From €75"
        description="Private taxi transfers from Limassol to Paphos Airport with professional drivers and comfortable vehicles."
        canonicalUrl="https://limassolairporttaxi.com/paphos-airport-taxi"
        ogUrl="https://limassolairporttaxi.com/paphos-airport-taxi"
      />
    }
  />

  <Route
    path="/limassol-to-nicosia"
    element={
      <AirportTransferPage
        airportName="Nicosia"
        routeTitle="Limassol to Nicosia Taxi"
        price="From €90"
        description="Comfortable private taxi transfers between Limassol and Nicosia available 24/7."
        canonicalUrl="https://limassolairporttaxi.com/limassol-to-nicosia"
        ogUrl="https://limassolairporttaxi.com/limassol-to-nicosia"
      />
    }
  />

  <Route
    path="/limassol-to-ayia-napa"
    element={
      <AirportTransferPage
        airportName="Ayia Napa"
        routeTitle="Limassol to Ayia Napa Taxi"
        price="From €120"
        description="Reliable long-distance taxi service from Limassol to Ayia Napa with fixed pricing."
        canonicalUrl="https://limassolairporttaxi.com/limassol-to-ayia-napa"
        ogUrl="https://limassolairporttaxi.com/limassol-to-ayia-napa"
      />
    }
  />

  <Route
    path="/limassol-to-protaras"
    element={
      <AirportTransferPage
        airportName="Protaras"
        routeTitle="Limassol to Protaras Taxi"
        price="From €130"
        description="Private taxi transfers between Limassol and Protaras with hotel pickup available."
        canonicalUrl="https://limassolairporttaxi.com/limassol-to-protaras"
        ogUrl="https://limassolairporttaxi.com/limassol-to-protaras"
      />
    }
  />

  <Route
    path="/limassol-to-troodos"
    element={
      <AirportTransferPage
        airportName="Troodos"
        routeTitle="Limassol to Troodos Taxi"
        price="From €80"
        description="Travel comfortably from Limassol to Troodos Mountains and traditional villages."
        canonicalUrl="https://limassolairporttaxi.com/limassol-to-troodos"
        ogUrl="https://limassolairporttaxi.com/limassol-to-troodos"
      />
    }
  />
</Routes>
        {!isAdminShell && <Footer />}
        {!isAdminShell && <MobileCTABar hidden={mobileMenuOpen} />}
        {!isAdminShell && <CookieConsentBanner />}
        {!isAdminShell && (
          <a
            href="https://wa.me/35796613191?text=Hello,%20I%20would%20like%20to%20book%20a%20taxi."
            onClick={() => trackWhatsAppClick("floating_whatsapp")}
            target="_blank"
            rel="noreferrer"
            className="fixed bottom-6 right-6 z-50 hidden items-center gap-2 rounded-full bg-green-500 px-5 py-4 font-bold text-white shadow-2xl transition hover:scale-105 hover:bg-green-600 md:flex"
          >
            <FaWhatsapp className="text-xl text-white" />
            <span className="hidden sm:inline">WhatsApp</span>
          </a>
        )}
      </div>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
